<template>
  <div class="reset-password mt-5">
    <img src="@/assets/Metals-Direct-Inc-logo.png" alt="" />
    <div class="container-fluid text-center">
      <div class="row justify-content-center">
        <div class="col-12 col-md-4">
          <h1 class="mt-4">Reset Password</h1>

          <!-- Loading state -->
          <div v-if="loading" class="mt-3">
            <circle-loader :size="40"></circle-loader>
            <p class="mt-2">Verifying reset link...</p>
          </div>

          <!-- Invalid/Expired token -->
          <div v-if="!loading && !validToken" class="alert alert-danger mt-3">
            <strong>Invalid or Expired Link</strong>
            <p class="mb-0 mt-2">
              This password reset link is invalid or has expired. Please request
              a new one.
            </p>
            <div class="mt-3">
              <router-link to="/forgot-password">Request New Link</router-link>
            </div>
          </div>

          <!-- Valid token - show form -->
          <div v-if="!loading && validToken && !resetComplete">
            <p class="text-left">
              Enter your new password for <strong>{{ userEmail }}</strong>
            </p>
            <b-form @submit.prevent="submitResetPassword">
              <label for="password">New Password</label>
              <b-input-group>
                <b-form-input
                  id="password"
                  v-model="password"
                  type="password"
                  :disabled="sending"
                  required
                  minlength="6"
                  v-show="!showPassword"
                ></b-form-input>
                <b-form-input
                  id="visiblePassword"
                  v-model="password"
                  type="text"
                  :disabled="sending"
                  required
                  minlength="6"
                  v-show="showPassword"
                ></b-form-input>
              </b-input-group>

              <label class="mt-3" for="confirmPassword"
                >Confirm New Password</label
              >
              <b-input-group>
                <b-form-input
                  id="confirmPassword"
                  v-model="confirmPassword"
                  type="password"
                  :disabled="sending"
                  required
                  minlength="6"
                  v-show="!showPassword"
                ></b-form-input>
                <b-form-input
                  id="visibleConfirmPassword"
                  v-model="confirmPassword"
                  type="text"
                  :disabled="sending"
                  required
                  minlength="6"
                  v-show="showPassword"
                ></b-form-input>
              </b-input-group>

              <div class="row text-left">
                <button
                  type="button"
                  class="show-pw-button mt-1 ml-2"
                  @click="showPassword = !showPassword"
                >
                  <span v-show="!showPassword">Show Password</span>
                  <span v-show="showPassword">Hide Password</span>
                </button>
              </div>

              <div
                v-if="
                  password && confirmPassword && password !== confirmPassword
                "
                class="alert alert-warning mt-2"
              >
                Passwords do not match
              </div>

              <b-button
                class="mt-3"
                type="submit"
                variant="primary"
                :disabled="sending || password !== confirmPassword"
              >
                <span v-if="sending">Resetting...</span>
                <span v-if="!sending">Reset Password</span>
              </b-button>
            </b-form>
          </div>

          <!-- Success message -->
          <div v-if="resetComplete" class="success-message">
            <div class="alert alert-success mt-3" role="alert">
              <strong>Password Reset Successful!</strong>
              <p class="mb-0 mt-2">
                Your password has been reset. You can now login with your new
                password.
              </p>
            </div>
            <div class="mt-3">
              <router-link to="/login">
                <b-button variant="primary" class="w-100">Go to Login</b-button>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import CircleLoader from '@/components/CircleLoader.vue'

export default {
  name: 'ResetPasswordPage',
  components: { CircleLoader },
  data: function () {
    return {
      token: '',
      password: '',
      confirmPassword: '',
      userEmail: '',
      loading: true,
      sending: false,
      validToken: false,
      resetComplete: false,
      showPassword: false,
    }
  },
  methods: {
    verifyToken() {
      this.token = this.$route.query.token

      if (!this.token) {
        this.loading = false
        this.validToken = false
        return
      }

      return axios
        .get(`/services/public/user/verify-reset-token/${this.token}`)
        .then(response => {
          this.validToken = true
          this.userEmail = response.data.email
          this.loading = false
        })
        .catch(error => {
          console.log('error: ', error)
          this.validToken = false
          this.loading = false
        })
    },
    submitResetPassword() {
      if (this.password !== this.confirmPassword) {
        this.$toasted.error('Passwords do not match', {
          action: {
            text: 'Close',
            onClick: (e, toastObject) => {
              toastObject.goAway(0)
            },
          },
        })
        return
      }

      if (this.password.length < 6) {
        this.$toasted.error('Password must be at least 6 characters', {
          action: {
            text: 'Close',
            onClick: (e, toastObject) => {
              toastObject.goAway(0)
            },
          },
        })
        return
      }

      this.sending = true
      const data = {
        token: this.token,
        password: this.password,
      }

      return axios
        .post('/services/public/user/reset-password', data)
        .then(response => {
          this.resetComplete = true
          this.sending = false
          this.$toasted.success('Password reset successful!', {
            duration: 4000,
          })
        })
        .catch(error => {
          console.log('error: ', error)
          this.sending = false
          this.$toasted.error(
            error?.response?.data?.error || 'Error resetting password',
            {
              action: {
                text: 'Close',
                onClick: (e, toastObject) => {
                  toastObject.goAway(0)
                },
              },
            },
          )
        })
    },
  },
  mounted() {
    this.verifyToken()
  },
}
</script>

<style lang="scss" scoped>
h1 {
  font-size: 24px;
}

label {
  text-align: left;
  font-weight: bold;
  display: block;
}

button {
  width: 100%;
}

.show-pw-button {
  width: 130px;
  border: none;
  background: none;
  color: #dc3545;
}

.success-message {
  text-align: left;
}

img {
  max-width: 300px;
  margin: 0 auto;
  display: block;
}
</style>


